// 飞书客户端内「免登」前端基元：判断是否在飞书容器内 + 通过 JSSDK 拿一次性预授权 code。
// 说明：
//  - h5sdk / tt 仅在飞书客户端内由容器注入（外部浏览器没有），故以其是否存在判定环境。
//  - 取 code 无需 h5sdk.config 鉴权签名，但必须在 h5sdk.ready 回调触发后调用。
//  - 拿到的 code 与「网页授权回调 ?code=」是同一种授权码，由后端 /auth/feishu/silent 换身份建会话。

// requestAccess / requestAuthCode 的 success/fail 回调载荷（按官方文档取关心字段）。
interface FeishuAuthResult {
  code?: string
  errno?: number
  errString?: string
}

interface FeishuTT {
  // 推荐：scopeList=[] 仅授予「获取登录用户信息」；注意入参是 appID（大写 ID）。
  requestAccess?: (args: {
    appID: string
    scopeList: string[]
    state?: string
    success: (res: FeishuAuthResult) => void
    fail: (err: FeishuAuthResult) => void
  }) => void
  // 兼容飞书 V6.9.0 以下版本的降级方案；注意入参是 appId（小写 d）。
  requestAuthCode?: (args: {
    appId: string
    success: (res: FeishuAuthResult) => void
    fail: (err: FeishuAuthResult) => void
  }) => void
}

interface FeishuH5Sdk {
  ready: (cb: () => void) => void
  error: (cb: (err: unknown) => void) => void
}

declare global {
  interface Window {
    h5sdk?: FeishuH5Sdk
    tt?: FeishuTT
  }
}

// 是否运行在飞书客户端内（h5sdk + tt 均由飞书容器注入）。
export const isFeishuEnv = (): boolean => typeof window !== 'undefined' && Boolean(window.h5sdk && window.tt)

// 「打开网页应用」applink：拉起飞书并在 H5 容器内打开网页应用。
// 文档：开发指南/AppLink 协议/已支持的协议/打开网页应用
const FEISHU_WEB_APP_OPEN = 'https://applink.feishu.cn/client/web_app/open'

/**
 * 构造在飞书容器内打开网页应用的 applink。
 * - appId：网页应用 App ID（cli_xxx）。
 * - targetUrl：要在 H5 容器内打开的完整 URL，默认当前页；按文档须整体 encode，故用 encodeURIComponent。
 *   lk_target_url 与 path 系列互斥，此处用前者以便携带 query / fragment。
 */
export const buildFeishuAppLink = (
  appId: string,
  targetUrl: string = typeof window !== 'undefined' ? window.location.href : '',
): string => `${FEISHU_WEB_APP_OPEN}?appId=${encodeURIComponent(appId)}&lk_target_url=${encodeURIComponent(targetUrl)}`

// 「打开聊天页面」applink：拉起飞书并打开与某人的单聊。
// 文档：开发指南/AppLink 协议/已支持的协议/打开聊天页面（飞书 3.9.0+）
// 只认 open_id（ou_ 开头）；库里存的是 union_id，故 open_id 由后端 user.card 实时换出。
const FEISHU_CHAT_OPEN = 'https://applink.feishu.cn/client/chat/open'

export const buildFeishuChatAppLink = (openId: string): string =>
  `${FEISHU_CHAT_OPEN}?openId=${encodeURIComponent(openId)}`

// 取 code 兜底超时：免登 JSAPI 无原生超时，避免极端下永远 pending。
const DEFAULT_TIMEOUT_MS = 8000

/**
 * 在飞书客户端内取一次性预授权 code（免登）。
 * 优先 requestAccess（飞书推荐，scopeList=[] 仅取登录身份）；任何失败都回落 requestAuthCode
 * （端内免登的规范 API，官方示例直接用它，且与 closeWindow 同属「无需网页应用鉴权」的 JSAPI）。
 * 非飞书环境 / 无对应 JSAPI / 超时 / 失败均 reject，由调用方回落手动登录。
 */
export const getFeishuAuthCode = (appId: string, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<string> =>
  new Promise((resolve, reject) => {
    const h5sdk = window.h5sdk
    const tt = window.tt
    if (!h5sdk || !tt) {
      reject(new Error('feishu_sdk_unavailable'))
      return
    }

    let settled = false
    const timer = setTimeout(
      () => finishReject(new Error('feishu_auth_timeout')),
      timeoutMs,
    )
    function finishResolve(code: string) {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(code)
    }
    function finishReject(err: Error) {
      if (settled) return
      settled = true
      clearTimeout(timer)
      reject(err)
    }

    const onSuccess = (res: FeishuAuthResult) => {
      if (res?.code) finishResolve(res.code)
      else finishReject(new Error('feishu_auth_empty_code'))
    }
    const toError = (e: unknown): Error => {
      const r = e as FeishuAuthResult | undefined
      return new Error(`feishu_auth_failed:${r?.errno ?? ''}:${r?.errString ?? ''}`)
    }

    // 全局错误回调（如 SDK 初始化失败）。
    h5sdk.error(err => finishReject(toError(err)))

    h5sdk.ready(() => {
      const requestViaAuthCode = () => {
        if (!tt.requestAuthCode) {
          finishReject(new Error('feishu_no_jsapi'))
          return
        }
        tt.requestAuthCode({ appId, success: onSuccess, fail: e => finishReject(toError(e)) })
      }

      if (tt.requestAccess) {
        tt.requestAccess({
          appID: appId,
          scopeList: [],
          success: onSuccess,
          fail: e => {
            // requestAccess 任何失败（版本过低 errno=103、scopeList 不被支持等）都回落 requestAuthCode：
            // 后者是端内免登的最小、规范 API；若它也失败，则由其 fail 携 errno/errString reject。
            console.warn('[feishu-silent] requestAccess failed, fallback to requestAuthCode:', e)
            requestViaAuthCode()
          },
        })
      } else {
        requestViaAuthCode()
      }
    })
  })
